import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Inbox, Loader2 } from "lucide-react";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { KIND_LABELS, STATUS_LABELS, PLATFORM_STATUS_LABELS, STATUS_TONES, formatDate } from "../components/help/requestLabels";

// Clinic admins: what the team asked Help for that MindConnect couldn't do
// (backend help/feature_requests.py). Review each one, talk to the person,
// then close it with a note or forward it to MindConnect.
const FILTERS = [
  { key: "open", label: "To review" },
  { key: "forwarded", label: "With MindConnect" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

function RequestRow({ r, onSaved }) {
  const [note, setNote] = useState(r.admin_note || "");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const open = r.status === "new" || r.status === "reviewing";

  const save = async (status) => {
    setBusy(status);
    setError(null);
    try {
      const res = await api.reviewHelpRequest(r.id, { status, note });
      onSaved(res.data.request);
    } catch (e) {
      setError(e?.response?.data?.error || "Couldn't save. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-xl border border-secondary-200 bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-secondary-900 font-medium">{r.question}</p>
          {r.details && <p className="text-sm text-secondary-700 mt-1 whitespace-pre-line">{r.details}</p>}
          <p className="text-xs text-secondary-500 mt-1">
            {r.user_label || "Team member"}{r.role ? ` (${r.role})` : ""} · {formatDate(r.created_at)} · {KIND_LABELS[r.kind] || r.kind}{r.feature_label ? `: ${r.feature_label}` : ""}
          </p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_TONES[r.status]}`}>{STATUS_LABELS[r.status]}</span>
      </div>

      {r.status === "forwarded" && (
        <p className="text-sm text-secondary-700 bg-secondary-50 rounded-lg px-3 py-2">
          <span className="font-medium">MindConnect: </span>
          {PLATFORM_STATUS_LABELS[r.platform_status] || "Received"}{r.platform_note ? ` - ${r.platform_note}` : ""}
          {r.admin_note && <span className="block text-secondary-500 mt-1">Your note: {r.admin_note}</span>}
        </p>
      )}
      {r.status === "closed" && r.admin_note && (
        <p className="text-sm text-secondary-700 bg-secondary-50 rounded-lg px-3 py-2"><span className="font-medium">Your note:</span> {r.admin_note}</p>
      )}

      {open && (
        <>
          <label className="block normal-case font-sans tracking-normal text-sm text-secondary-700">
            Note <span className="text-secondary-400">(the person who asked sees it)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2}
              placeholder="e.g. Spoke to them: they need this for couples sessions"
              className="mt-1 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm text-secondary-900 focus:ring-2 focus:ring-primary-500" />
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex flex-wrap gap-2">
            {r.status === "new" && (
              <button type="button" onClick={() => save("reviewing")} disabled={!!busy}
                className="px-3 py-2 rounded-lg border border-secondary-300 text-sm text-secondary-800 hover:bg-secondary-50 disabled:opacity-60">
                {busy === "reviewing" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "I'm looking at it"}
              </button>
            )}
            <button type="button" onClick={() => save("forwarded")} disabled={!!busy}
              className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60">
              {busy === "forwarded" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Forward to MindConnect"}
            </button>
            <button type="button" onClick={() => save("closed")} disabled={!!busy}
              className="px-3 py-2 rounded-lg border border-secondary-300 text-sm text-secondary-800 hover:bg-secondary-50 disabled:opacity-60">
              {busy === "closed" ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Close"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function TeamRequests() {
  const [filter, setFilter] = useState("open");
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.fetchTeamHelpRequests()
      .then((r) => setRows(r.data.requests || []))
      .catch((e) => setError(e?.response?.data?.error || "Couldn't load requests."));
  }, []);
  useEffect(load, [load]);

  const shown = (rows || []).filter((r) =>
    filter === "all" ? true : filter === "open" ? ["new", "reviewing"].includes(r.status) : r.status === filter);
  const openCount = (rows || []).filter((r) => ["new", "reviewing"].includes(r.status)).length;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 pb-12">
        <Link to="/help" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Help
        </Link>
        <div className="flex items-start gap-3">
          <Inbox className="w-7 h-7 text-primary-600 shrink-0" />
          <div>
            <h1 className="text-2xl font-heading font-bold text-secondary-900">Team requests</h1>
            <p className="text-secondary-600 mt-1">
              What your team asked Help for that MindConnect couldn't do. Talk to the person, add a note, then close it
              or forward it to MindConnect. Requests you send yourself go straight to MindConnect.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)}
              className={`text-sm px-3 py-1.5 rounded-full border ${filter === f.key ? "bg-primary-600 text-white border-primary-600" : "bg-white text-secondary-700 border-secondary-300 hover:border-primary-400"}`}>
              {f.label}{f.key === "open" && openCount ? ` (${openCount})` : ""}
            </button>
          ))}
        </div>

        {error && <p className="text-red-600">{error}</p>}
        {!rows && !error && <p className="text-secondary-500">Loading…</p>}
        {rows && shown.length === 0 && (
          <p className="text-secondary-500">{filter === "open" ? "Nothing waiting for you." : "No requests here."}</p>
        )}
        <div className="space-y-3">
          {shown.map((r) => (
            <RequestRow key={r.id} r={r} onSaved={(u) => setRows((all) => all.map((x) => (x.id === u.id ? u : x)))} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
